import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { authService } from '../services/authService';
import { setUser, setToken } from '../store/slices/authSlice';
import { FieldError, inputErrorClass } from '../components/FieldError';
import { SessionReplacedBanner } from '../components/SessionReplacedBanner';
import { BackArrowIcon } from '../components/BackArrowIcon';

export function SignInPage() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const submit = async () => {
    setErrors({});
    const e: Record<string, string> = {};
    if (!email.trim()) e.email = 'Email is required.';
    if (!password) e.password = 'Password is required.';
    if (Object.keys(e).length) {
      setErrors(e);
      return;
    }
    setLoading(true);
    try {
      const res = await authService.signIn({ email, password });
      dispatch(setUser(res.user));
      dispatch(setToken(res.token));
      navigate('/home');
    } catch (err: unknown) {
      setErrors({ submit: err instanceof Error ? err.message : 'Sign in failed' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white px-6 py-8 max-w-lg mx-auto">
      <SessionReplacedBanner />
      <Link
        to="/signin"
        className="mb-8 inline-flex items-center gap-1.5 text-sm font-medium text-slate-500"
      >
        <BackArrowIcon />
        Back
      </Link>
      <h1 className="text-3xl font-black text-slate-900 mb-2">Welcome Back</h1>
      <p className="text-slate-500 mb-8">Please sign in to your account</p>

      <label className="block text-xs font-black text-blue-600 uppercase mb-2">Email</label>
      <input
        className={`w-full rounded-2xl bg-slate-100 px-4 py-4 mb-1 ${errors.email ? inputErrorClass : ''}`}
        placeholder="name@company.com"
        value={email}
        onChange={(ev) => {
          setEmail(ev.target.value);
          setErrors((o) => {
            const n = { ...o };
            delete n.email;
            return n;
          });
        }}
        type="email"
        autoComplete="email"
      />
      <FieldError message={errors.email} />

      <label className="block text-xs font-black text-blue-600 uppercase mb-2 mt-4">Password</label>
      <input
        className={`w-full rounded-2xl bg-slate-100 px-4 py-4 mb-1 ${errors.password ? inputErrorClass : ''}`}
        placeholder="password"
        type="password"
        value={password}
        onChange={(ev) => {
          setPassword(ev.target.value);
          setErrors((o) => {
            const n = { ...o };
            delete n.password;
            return n;
          });
        }}
        autoComplete="current-password"
      />
      <FieldError message={errors.password} />

      <div className="text-right mt-2 mb-6">
        <Link to="/forgot-password" className="text-[10px] font-black text-blue-400 uppercase">
          Forgot Password?
        </Link>
      </div>

      <FieldError message={errors.submit} />
      <button
        type="button"
        disabled={loading}
        onClick={submit}
        className="w-full py-5 rounded-2xl bg-blue-600 text-white font-black text-lg disabled:opacity-50"
      >
        {loading ? '...' : 'Sign In'}
      </button>
      <p className="text-center text-xs text-slate-400 mt-8">
        <Link to="/terms" className="text-blue-600 font-semibold hover:underline">
          Terms of Use
        </Link>
        {' · '}
        <Link to="/privacy" className="text-blue-600 font-semibold hover:underline">
          Privacy Policy
        </Link>
      </p>
    </div>
  );
}
